'use client';

import { useMemo, useState, ReactNode, useEffect } from 'react';
import {
  Button,
  Spinner,
  Card,
  CardBody,
  Avatar,
  Select,
  SelectItem,
} from '@heroui/react';
import {
  OptimalSlotType,
  SlotOptPopulated,
} from '@/util/types/optimalSlotType';
import { useTZDayjs } from '@/context/TimezoneContext';
import {
  FaChevronLeft,
  FaChevronRight,
  FaCheck,
  FaStar,
} from 'react-icons/fa6';
import { Dayjs } from 'dayjs';
import { MAX_VISIBLE_SLOTS_PER_DAY } from '../constants';
import { useScheduler } from '../SchedulerContext';

type Inspector = {
  id: string;
  name: string;
  thumb: string;
  score?: number;
};

type TimeSlot = {
  datetime: string;
  inspectors: Inspector[];
  score: number;
  rank?: 1 | 2 | 3;
};

type CalendarViewProps = {
  currentOptSlots: OptimalSlotType | null;
  isLoading: boolean;
  isBackgroundLoading?: boolean;
  selectedDateTime: string | null;
  onTimeSlotSelect: (datetime: string, inspectorId?: string) => void;
  startDate: Dayjs;
  onWeekChange: (newStartDate: Dayjs) => void;
  selectedInspectorIds: string[];
  topRecommendations: SlotOptPopulated[];
  inspectorFilter: ReactNode;
  alwaysShowInspectors?: boolean;
  selectedInspectorForSlot?: Record<string, string>;
  autoSelectTopInspector?: boolean;
  emptyStateContent?: ReactNode;
  weekNavRightContent?: ReactNode;
  /** Optional date (YYYY-MM-DD) to lightly highlight on the calendar (e.g. from a "go to" picker). Falls back to selectedDateTime's date when not provided. */
  highlightDateKey?: string | null;
  /**
   * Which surface is rendering this calendar.
   *
   * The Online Booking Cutoff Time is a client-facing guardrail, and this component
   * re-applies it below as defense in depth — the route response is cached for up to
   * 5 minutes, so a client could otherwise be shown next-day slots that were fetched
   * before the cutoff passed. Internal staff surfaces are exempt from the cutoff
   * entirely, so they must opt out or the suppression silently undoes the backend's
   * exemption and tomorrow disappears again.
   *
   * Defaults to `'internal'`: the backend is authoritative for online callers, so an
   * online consumer that forgets this prop still gets suppression from the API, while
   * an internal consumer that forgets it does not lose availability.
   */
  scheduledLocation?: 'internal' | 'online';
};

export default function CalendarView({
  currentOptSlots,
  isLoading,
  isBackgroundLoading = false,
  selectedDateTime,
  onTimeSlotSelect,
  startDate,
  onWeekChange,
  selectedInspectorIds,
  topRecommendations,
  inspectorFilter,
  alwaysShowInspectors = false,
  selectedInspectorForSlot = {},
  autoSelectTopInspector = false,
  emptyStateContent,
  weekNavRightContent,
  highlightDateKey: highlightDateKeyProp,
  scheduledLocation = 'internal',
}: CalendarViewProps) {
  /** Client-facing surfaces enforce the Online Booking Cutoff Time; internal ones don't. */
  const applyBookingCutoff = scheduledLocation === 'online';
  const tzDayjs = useTZDayjs();
  const { scheduleSettings, companyStyles } = useScheduler();
  const [expandedSlot, setExpandedSlot] = useState<string | null>(null);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [isDesktop, setIsDesktop] = useState(false);

  // Detect desktop breakpoint (md = 768px)
  useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkIsDesktop();
    window.addEventListener('resize', checkIsDesktop);
    return () => window.removeEventListener('resize', checkIsDesktop);
  }, []);

  // Calculate calendar days: 7 for mobile, 14 for desktop
  const calendarDays = isDesktop ? 14 : 7;

  const selectedDateKey =
    highlightDateKeyProp ??
    (selectedDateTime
      ? tzDayjs(selectedDateTime).format('YYYY-MM-DD')
      : null);

  // Create map of recommended slots for quick lookup
  const recommendedSlotMap = useMemo(() => {
    const map = new Map<string, 1 | 2 | 3>();
    const now = tzDayjs();
    const tomorrow = now.add(1, 'day').startOf('day');
    const cutoffMinutes = scheduleSettings?.nextDayCutoffTimeInMinutes;
    const displayDayOfSlots = scheduleSettings?.displayDayOfSlots;

    topRecommendations.forEach((slot, index) => {
      if (index < 3) {
        const slotTime = tzDayjs(slot.datetime);

        // Once the cutoff time has passed, hide next-day slots, and same-day
        // slots when day-of slots are enabled (mirrors backend suppression).
        const slotDateStr = slotTime.format('YYYY-MM-DD');
        const todayStr = now.format('YYYY-MM-DD');
        const tomorrowStr = tomorrow.format('YYYY-MM-DD');
        if (
          applyBookingCutoff &&
          cutoffMinutes !== undefined &&
          cutoffMinutes !== null &&
          cutoffMinutes >= 0
        ) {
          const currentMinutes = now.hour() * 60 + now.minute();
          if (currentMinutes >= cutoffMinutes) {
            if (slotDateStr === tomorrowStr) {
              return; // Past cutoff — hide all next-day slots
            }
            if (displayDayOfSlots && slotDateStr === todayStr) {
              return; // Past cutoff — hide all same-day slots
            }
          }
        }

        map.set(slot.datetime, (index + 1) as 1 | 2 | 3);
      }
    });
    return map;
  }, [topRecommendations, tzDayjs, scheduleSettings]);

  // Group time slots by date, filtering by selected inspectors if any
  const slotsByDate = useMemo(() => {
    if (!currentOptSlots?.slotsSorted) return {};

    const grouped: Record<string, TimeSlot[]> = {};
    const now = tzDayjs();
    const tomorrow = now.add(1, 'day').startOf('day');
    const cutoffMinutes = scheduleSettings?.nextDayCutoffTimeInMinutes;
    const displayDayOfSlots = scheduleSettings?.displayDayOfSlots;

    currentOptSlots.slotsSorted.forEach((slot) => {
      const slotTime = tzDayjs(slot.datetime);

      // Skip slots that are in the past
      if (slotTime.isBefore(now)) {
        return;
      }

      // Once the cutoff time has passed, hide next-day slots, and same-day
      // slots when day-of slots are enabled (mirrors backend suppression).
      // Skipped on internal surfaces, which are exempt from the cutoff.
      if (
        applyBookingCutoff &&
        cutoffMinutes !== undefined &&
        cutoffMinutes !== null &&
        cutoffMinutes >= 0
      ) {
        const currentMinutes = now.hour() * 60 + now.minute();
        if (currentMinutes >= cutoffMinutes) {
          const slotDateStr = slotTime.format('YYYY-MM-DD');
          const todayStr = now.format('YYYY-MM-DD');
          const tomorrowStr = tomorrow.format('YYYY-MM-DD');
          if (slotDateStr === tomorrowStr) {
            return; // Past cutoff — hide all next-day slots
          }
          if (displayDayOfSlots && slotDateStr === todayStr) {
            return; // Past cutoff — hide all same-day slots
          }
        }
      }

      // Skip if inspector filter is active and this slot doesn't match
      if (
        selectedInspectorIds.length > 0 &&
        !selectedInspectorIds.includes(slot._inspectorId._id)
      ) {
        return;
      }

      const dateKey = slotTime.format('YYYY-MM-DD');

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }

      // Check if this time already exists
      const existing = grouped[dateKey].find(
        (g) => g.datetime === slot.datetime
      );

      const inspectorInfo = {
        id: slot._inspectorId._id,
        name: `${slot._inspectorId.firstName} ${slot._inspectorId.lastName}`,
        thumb: slot._inspectorId.thumb,
        score: slot.score,
      };

      if (existing) {
        // Add this inspector to the existing slot if not already present
        const existingInspector = existing.inspectors.find(
          (i) => i.id === inspectorInfo.id
        );
        if (!existingInspector) {
          existing.inspectors.push(inspectorInfo);
        } else {
          // Update score if this one is higher
          if (inspectorInfo.score && existingInspector.score) {
            existingInspector.score = Math.max(
              existingInspector.score,
              inspectorInfo.score
            );
          } else if (inspectorInfo.score) {
            existingInspector.score = inspectorInfo.score;
          }
        }
        // Update slot score if this one is higher
        if (slot.score > existing.score) {
          existing.score = slot.score;
        }
      } else {
        grouped[dateKey].push({
          datetime: slot.datetime,
          inspectors: [inspectorInfo],
          score: slot.score,
          rank: recommendedSlotMap.get(slot.datetime),
        });
      }
    });

    // Sort time slots within each day
    Object.keys(grouped).forEach((dateKey) => {
      grouped[dateKey].sort((a, b) =>
        tzDayjs(a.datetime).diff(tzDayjs(b.datetime))
      );
    });

    return grouped;
  }, [
    currentOptSlots,
    tzDayjs,
    selectedInspectorIds,
    recommendedSlotMap,
    scheduleSettings,
  ]);

  // Generate days starting from startDate (for desktop)
  const days = useMemo(() => {
    const daysList: Dayjs[] = [];
    for (let i = 0; i < calendarDays; i++) {
      daysList.push(startDate.add(i, 'day'));
    }
    return daysList;
  }, [startDate, calendarDays]);

  // For mobile, start from today if startDate is in the past
  const mobileStartDate = useMemo(() => {
    const today = tzDayjs().startOf('day');
    return startDate.isBefore(today) ? today : startDate;
  }, [startDate, tzDayjs]);

  const mobileDays = useMemo(() => {
    const daysList: Dayjs[] = [];
    for (let i = 0; i < 7; i++) {
      daysList.push(mobileStartDate.add(i, 'day'));
    }
    return daysList;
  }, [mobileStartDate]);

  // Calculate date range for header (desktop)
  const dateRange = useMemo(() => {
    const endDate = startDate.add(calendarDays - 1, 'day');
    const startMonth = startDate.format('MMM');
    const endMonth = endDate.format('MMM');
    const year = startDate.format('YYYY');

    if (startMonth === endMonth) {
      return `${startMonth} ${startDate.format('D')} - ${endDate.format(
        'D'
      )}, ${year}`;
    } else {
      return `${startDate.format('MMM D')} - ${endDate.format(
        'MMM D'
      )}, ${year}`;
    }
  }, [startDate, calendarDays]);

  // Calculate date range for mobile header
  const mobileDateRange = useMemo(() => {
    const endDate = mobileStartDate.add(6, 'day');
    const startMonth = mobileStartDate.format('MMM');
    const endMonth = endDate.format('MMM');
    const year = mobileStartDate.format('YYYY');

    if (startMonth === endMonth) {
      return `${startMonth} ${mobileStartDate.format('D')} - ${endDate.format(
        'D'
      )}, ${year}`;
    } else {
      return `${mobileStartDate.format('MMM D')} - ${endDate.format(
        'MMM D'
      )}, ${year}`;
    }
  }, [mobileStartDate]);

  const handlePrev = () => {
    onWeekChange(startDate.subtract(calendarDays, 'day'));
  };

  const handleNext = () => {
    onWeekChange(startDate.add(calendarDays, 'day'));
  };

  const handleToday = () => {
    // Go to Monday of current week
    onWeekChange(tzDayjs().startOf('week').add(1, 'day'));
  };

  const isCurrentPeriod = useMemo(() => {
    const today = tzDayjs().startOf('day');
    const endOfPeriod = startDate.add(calendarDays - 1, 'day').endOf('day');
    return (
      (today.isAfter(startDate) || today.isSame(startDate, 'day')) &&
      (today.isBefore(endOfPeriod) || today.isSame(endOfPeriod, 'day'))
    );
  }, [startDate, tzDayjs, calendarDays]);

  const handleSlotClick = (slot: TimeSlot) => {
    if (slot.inspectors.length === 1) {
      // Single inspector - select directly
      onTimeSlotSelect(slot.datetime, slot.inspectors[0].id);
      setExpandedSlot(null);
    } else {
      // Multiple inspectors - check if we should show dropdown or auto-select
      if (!autoSelectTopInspector) {
        // If autoSelectTopInspector is false, always show dropdown
        if (expandedSlot === slot.datetime) {
          setExpandedSlot(null);
        } else {
          setExpandedSlot(slot.datetime);
        }
      } else {
        // autoSelectTopInspector is true - check if we should show dropdown based on toggle
        const shouldShowDropdown = alwaysShowInspectors || scheduleSettings.showInspectorInOnlineScheduler;

        if (shouldShowDropdown) {
          // Show dropdown when toggle is on or alwaysShowInspectors is true
          if (expandedSlot === slot.datetime) {
            setExpandedSlot(null);
          } else {
            setExpandedSlot(slot.datetime);
          }
        } else {
          // Auto-select the best inspector (highest score) when toggle is off
          const bestInspectorId = getBestInspectorForSlot(slot.datetime);
          if (bestInspectorId) {
            onTimeSlotSelect(slot.datetime, bestInspectorId);
            setExpandedSlot(null);
          } else {
            // Fallback to first inspector if best not found
            onTimeSlotSelect(slot.datetime, slot.inspectors[0].id);
            setExpandedSlot(null);
          }
        }
      }
    }
  };

  const handleInspectorSelect = (datetime: string, inspectorId: string) => {
    onTimeSlotSelect(datetime, inspectorId);
    setExpandedSlot(null);
  };

  // Get the best inspector (highest score) for a given datetime
  const getBestInspectorForSlot = (datetime: string): string | null => {
    if (!currentOptSlots?.slotsSorted) return null;

    const matchingSlots = currentOptSlots.slotsSorted.filter(
      (slot) => slot.datetime === datetime
    );

    if (matchingSlots.length === 0) return null;

    const bestSlot = matchingSlots.reduce((best, current) =>
      current.score > best.score ? current : best
    );

    return bestSlot._inspectorId._id;
  };

  const toggleDayExpansion = (dateKey: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  };

  // Helper function to render skeleton slots for loading state
  const renderSkeletonSlots = () => {
    return (
      <div className='space-y-1'>
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className='h-12 w-full animate-pulse rounded bg-gray-200'
          />
        ))}
      </div>
    );
  };

  // Helper function to render a day cell
  const renderDayCell = (day: Dayjs, index: number) => {
    const dateKey = day.format('YYYY-MM-DD');
    const slotsForDay = slotsByDate[dateKey] || [];
    const isToday = day.isSame(tzDayjs(), 'day');
    const isPast = day.isBefore(tzDayjs(), 'day');
    const dayName = day.format('ddd');
    const dayNumber = day.format('D');
    const fullDate = day.format('MMMM D, YYYY');

    const isSelectedDate = selectedDateKey === dateKey;
    const isDayExpanded = expandedDays.has(dateKey);
    return (
      <div
        key={index}
        className={`min-h-[240px] border-r p-2 last:border-r-0 ${isPast
          ? 'bg-gray-50 bg-[linear-gradient(45deg,transparent_48%,rgba(0,0,0,0.08)_48%,rgba(0,0,0,0.08)_52%,transparent_52%)] bg-size-[8px_8px] opacity-60'
          : isSelectedDate
            ? 'bg-warning-50'
            : ''
          }`}
        role='gridcell'
        aria-label={`${fullDate}${isToday ? ' (Today)' : ''}${isSelectedDate ? ' (Selected date)' : ''}`}
      >
        {/* Day header */}
        <div className='mb-2 text-center'>
          <div className='text-xs font-medium text-gray-600'>{dayName}</div>
          <div
            className={`mx-auto mt-1 flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${isToday ? 'text-white' : 'text-gray-700'
              }`}
            style={
              isToday
                ? {
                  backgroundColor:
                    companyStyles.colorSecondary ||
                    companyStyles.companyColor ||
                    '#006FEE',
                }
                : undefined
            }
          >
            {dayNumber}
          </div>
        </div>

        {/* Skeleton loading state */}
        {isLoading && !isPast && renderSkeletonSlots()}

        {/* Time slots for this day */}
        {!isLoading && !isPast && slotsForDay.length > 0 && (
          <div className='space-y-1'>
            {(isDayExpanded ? slotsForDay : slotsForDay.slice(0, MAX_VISIBLE_SLOTS_PER_DAY)).map((slot) => {
              const isSelected = selectedDateTime === slot.datetime;
              const isExpanded = expandedSlot === slot.datetime;
              const slotTime = tzDayjs(slot.datetime);
              const slotLabel = `${slotTime.format(
                'h:mm A'
              )} on ${slotTime.format('MMMM D, YYYY')}`;

              return (
                <div key={slot.datetime}>
                  <button
                    onClick={() => handleSlotClick(slot)}
                    className={`relative cursor-pointer w-full rounded px-1.5 py-1.5 text-left text-xs transition-all hover:shadow-sm ${isSelected ? 'text-white shadow-md' : ''
                      }`}
                    style={
                      isSelected
                        ? {
                          backgroundColor:
                            companyStyles.colorSecondary ||
                            companyStyles.companyColor ||
                            '#006FEE',
                          borderColor:
                            companyStyles.colorSecondary ||
                            companyStyles.companyColor ||
                            '#006FEE',
                          borderWidth: '1px',
                        }
                        : {
                          backgroundColor: companyStyles.companyColor
                            ? `${companyStyles.companyColor}33`
                            : 'rgba(0, 111, 238, 0.2)',
                          color: companyStyles.companyColor
                            ? `${companyStyles.companyColor}` // ~70% opacity = even darker
                            : 'rgba(0, 111, 238, 0.7)',
                        }
                    }
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor =
                          companyStyles.companyColor
                            ? `${companyStyles.companyColor}1A`
                            : 'rgba(0, 111, 238, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor =
                          companyStyles.companyColor
                            ? `${companyStyles.companyColor}33`
                            : 'rgba(0, 111, 238, 0.2)';
                      }
                    }}
                    aria-label={`${isSelected ? 'Selected: ' : 'Select time slot: '
                      }${slotLabel}`}
                    aria-pressed={isSelected}
                  >
                    <div className='flex items-center justify-between gap-1'>
                      <div className='flex items-center gap-1.5 overflow-hidden'>
                        {/* Stacked avatars - show if alwaysShowInspectors is true OR showInspectorInOnlineScheduler is true OR inspector filter has selections */}
                        {(alwaysShowInspectors ||
                          scheduleSettings.showInspectorInOnlineScheduler ||
                          selectedInspectorIds.length > 0) && (
                            <div className='flex -space-x-1.5'>
                              {slot.inspectors
                                .slice(0, 3)
                                .map((inspector, idx) => (
                                  <Avatar
                                    key={inspector.id}
                                    src={inspector.thumb}
                                    alt={inspector.name}
                                    size='sm'
                                    className='h-4 w-4 shrink-0 ring-1 ring-white'
                                    style={{ zIndex: 3 - idx }}
                                  />
                                ))}
                            </div>
                          )}

                        <div className='flex flex-col overflow-hidden'>
                          <div className='flex items-center gap-1'>
                            <span className='font-semibold leading-tight'>
                              {slotTime.format('h:mm A')}
                            </span>
                          </div>
                          {/* Inspector name - show if alwaysShowInspectors is true OR showInspectorInOnlineScheduler is true OR inspector filter has selections */}
                          {(alwaysShowInspectors ||
                            scheduleSettings.showInspectorInOnlineScheduler ||
                            selectedInspectorIds.length > 0) && (
                              <span
                                className={`truncate text-[10px] leading-tight ${isSelected ? 'text-white/90' : ''
                                  }`}
                                style={
                                  !isSelected
                                    ? {
                                      color: companyStyles.companyColor
                                        ? `${companyStyles.companyColor}CC`
                                        : 'rgba(0, 111, 238, 0.8)',
                                    }
                                    : undefined
                                }
                              >
                                {slot.inspectors.length === 1
                                  ? slot.inspectors[0].name.split(' ')[0]
                                  : `${slot.inspectors.length} available`}
                              </span>
                            )}
                        </div>
                      </div>
                      {isSelected && (
                        <FaCheck
                          size={10}
                          aria-hidden='true'
                          className='shrink-0'
                        />
                      )}
                    </div>
                  </button>

                  {/* Inspector dropdown for multi-inspector slots - show if alwaysShowInspectors is true OR toggle is enabled OR autoSelectTopInspector is false */}
                  {isExpanded && slot.inspectors.length > 1 && (!autoSelectTopInspector || alwaysShowInspectors || scheduleSettings.showInspectorInOnlineScheduler) && (
                    <div
                      className='mt-1 overflow-hidden rounded border bg-white p-2 shadow-md'
                      style={{
                        borderColor: companyStyles.colorSecondary
                          ? `${companyStyles.colorSecondary}33`
                          : companyStyles.companyColor
                            ? `${companyStyles.companyColor}33`
                            : 'rgba(0, 111, 238, 0.2)',
                      }}
                    >
                      <p className='mb-1 text-[10px] font-semibold text-gray-600'>
                        Select Inspector:
                      </p>

                      <div className='space-y-1'>
                        {/* Sort inspectors by score (highest first), then by name */}
                        {[...slot.inspectors]
                          .sort((a, b) => {
                            // First sort by score (higher is better)
                            if (a.score && b.score) {
                              if (b.score !== a.score) {
                                return b.score - a.score;
                              }
                            } else if (a.score && !b.score) {
                              return -1;
                            } else if (!a.score && b.score) {
                              return 1;
                            }
                            // If scores are equal or both missing, sort by name
                            return a.name.localeCompare(b.name);
                          })
                          .map((inspector) => {
                            const isBest =
                              inspector.id ===
                              getBestInspectorForSlot(slot.datetime);
                            const isSelected =
                              selectedInspectorForSlot[slot.datetime] ===
                              inspector.id;
                            return (
                              <button
                                key={inspector.id}
                                onClick={() =>
                                  handleInspectorSelect(
                                    slot.datetime,
                                    inspector.id
                                  )
                                }
                                className={`flex w-full items-center gap-2 rounded p-1.5 text-left transition-colors ${isSelected
                                  ? 'bg-warning-50 border border-warning-300'
                                  : ''
                                  }`}
                                style={
                                  isSelected
                                    ? undefined
                                    : {
                                      color: 'inherit',
                                    }
                                }
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor =
                                      companyStyles.colorSecondary
                                        ? `${companyStyles.colorSecondary}0D`
                                        : companyStyles.companyColor
                                          ? `${companyStyles.companyColor}0D`
                                          : 'rgba(0, 111, 238, 0.05)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = '';
                                  }
                                }}
                              >
                                <Avatar
                                  src={inspector.thumb}
                                  alt={inspector.name}
                                  size='sm'
                                  className='h-5 w-5 shrink-0'
                                />
                                <span
                                  // min-w-0 + truncate: in narrow columns (e.g. the client
                                  // quote portal) the name must shrink instead of pushing
                                  // the star/check icons outside the dropdown.
                                  className={`min-w-0 flex-1 truncate text-xs ${isSelected
                                    ? 'font-semibold text-warning-900'
                                    : 'text-gray-700'
                                    }`}
                                  title={inspector.name}
                                >
                                  {inspector.name}
                                </span>
                                {isBest && (
                                  <FaStar
                                    size={10}
                                    className='shrink-0 text-warning-500'
                                    aria-label='Top choice'
                                  />
                                )}
                                {isSelected && (
                                  <FaCheck
                                    size={10}
                                    className='shrink-0 text-warning-700'
                                    aria-label='Selected'
                                  />
                                )}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {slotsForDay.length > MAX_VISIBLE_SLOTS_PER_DAY && (
              <button
                onClick={() => toggleDayExpansion(dateKey)}
                className='w-full rounded px-1.5 py-1 text-center text-[10px] font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700'
                aria-label={
                  isDayExpanded
                    ? `Show fewer times for ${fullDate}`
                    : `Show ${slotsForDay.length - MAX_VISIBLE_SLOTS_PER_DAY} more times for ${fullDate}`
                }
                aria-expanded={isDayExpanded}
              >
                {isDayExpanded
                  ? 'Show less'
                  : `+${slotsForDay.length - MAX_VISIBLE_SLOTS_PER_DAY} more`}
              </button>
            )}
          </div>
        )}

        {/* No slots message */}
        {!isLoading && !isPast && slotsForDay.length === 0 && (
          <div className='mt-4 text-center text-[10px] text-gray-400'>
            No slots
          </div>
        )}

        {/* Past message */}
        {isPast && (
          <div className='mt-4 text-center text-[10px] text-gray-400'>Past</div>
        )}
      </div>
    );
  };

  // Only show full-screen spinner on initial load (no data yet)
  if (isLoading && !currentOptSlots) {
    return (
      <div className='flex min-h-[600px] items-center justify-center'>
        <div className='flex flex-col items-center gap-4'>
          <Spinner size='lg' color='warning' />
          <p className='text-gray-600'>Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Calendar Header with Inspector Filter */}
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-center gap-3'>
          <h3 className='text-xl font-bold'>
            <span className='md:hidden'>{mobileDateRange}</span>
            <span className='hidden md:inline'>{dateRange}</span>
          </h3>
          {isBackgroundLoading && <Spinner size='sm' color='warning' />}
        </div>

        <div className='flex flex-row justify-between gap-2 sm:items-center sm:justify-start'>
          {/* Inspector Filter */}
          <div className='shrink-0'>{inspectorFilter}</div>

          {/* Navigation: optional left content (e.g. Go to week) + buttons */}
          <div className='flex items-center gap-2'>
            {weekNavRightContent}
            {!isCurrentPeriod && (
              <Button
                size='sm'
                variant='flat'
                onPress={handleToday}
                className='font-medium'
                aria-label='Go to current week'
              >
                Today
              </Button>
            )}
            <Button
              size='sm'
              variant='flat'
              isIconOnly
              onPress={handlePrev}
              aria-label={
                calendarDays === 14 ? 'Previous 2 weeks' : 'Previous week'
              }
            >
              <FaChevronLeft />
            </Button>
            <Button
              size='sm'
              variant='flat'
              isIconOnly
              onPress={handleNext}
              aria-label={calendarDays === 14 ? 'Next 2 weeks' : 'Next week'}
            >
              <FaChevronRight />
            </Button>
          </div>
        </div>
      </div>

      {/* Calendar grid + legend; empty state overlays with blur when provided */}
      <div className='relative'>
        <>
          {/* Mobile Stacked View */}
          <div className='md:hidden'>
            <div className='rounded-lg border bg-white'>
              {mobileDays.map((day, index) => {
                const dateKey = day.format('YYYY-MM-DD');
                const slotsForDay = slotsByDate[dateKey] || [];
                const isToday = day.isSame(tzDayjs(), 'day');
                const isPast = day.isBefore(tzDayjs(), 'day');
                const dayName = day.format('ddd');
                const dayNumber = day.format('D');
                const fullDate = day.format('MMMM D, YYYY');
                const isSelectedDate = selectedDateKey === dateKey;
                return (
                  <div
                    key={index}
                    className={`border-b p-3 last:border-b-0 ${isPast
                      ? 'bg-gray-50 bg-[linear-gradient(45deg,transparent_48%,rgba(0,0,0,0.08)_48%,rgba(0,0,0,0.08)_52%,transparent_52%)] bg-size-[8px_8px] opacity-60'
                      : isSelectedDate
                        ? 'bg-warning-50'
                        : ''
                      }`}
                    role='gridcell'
                    aria-label={`${fullDate}${isToday ? ' (Today)' : ''}${isSelectedDate ? ' (Selected date)' : ''}`}
                  >
                    {/* Day Header */}
                    <div className='mb-2 flex items-center justify-between'>
                      <div className='flex items-center gap-2'>
                        <div
                          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isToday ? 'text-white' : 'text-gray-700'
                            }`}
                          style={
                            isToday
                              ? {
                                backgroundColor:
                                  companyStyles.colorSecondary ||
                                  companyStyles.companyColor ||
                                  '#006FEE',
                              }
                              : {
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                              }
                          }
                        >
                          {dayNumber}
                        </div>
                        <div className='text-xs font-medium text-gray-600'>
                          {dayName}
                        </div>
                      </div>
                      {isPast && (
                        <span className='text-[10px] text-gray-400'>Past</span>
                      )}
                    </div>

                    {/* Skeleton loading state */}
                    {isLoading && !isPast && renderSkeletonSlots()}

                    {/* Time slots for this day */}
                    {!isLoading && !isPast && slotsForDay.length > 0 && (
                      <div className='space-y-2'>
                        {slotsForDay.map((slot) => {
                          const isSelected = selectedDateTime === slot.datetime;
                          const isExpanded = expandedSlot === slot.datetime;
                          const slotTime = tzDayjs(slot.datetime);
                          const slotLabel = `${slotTime.format(
                            'h:mm A'
                          )} on ${slotTime.format('MMMM D, YYYY')}`;

                          return (
                            <div key={slot.datetime}>
                              <button
                                onClick={() => handleSlotClick(slot)}
                                className={`relative w-full rounded-lg px-3 py-2.5 text-left transition-all ${isSelected
                                  ? 'text-white shadow-md'
                                  : 'bg-gray-50 hover:bg-gray-100'
                                  }`}
                                style={
                                  isSelected
                                    ? {
                                      backgroundColor:
                                        companyStyles.colorSecondary ||
                                        companyStyles.companyColor ||
                                        '#006FEE',
                                      borderColor:
                                        companyStyles.colorSecondary ||
                                        companyStyles.companyColor ||
                                        '#006FEE',
                                      borderWidth: '2px',
                                    }
                                    : {
                                      backgroundColor:
                                        companyStyles.companyColor
                                          ? `${companyStyles.companyColor}1A`
                                          : 'rgba(0, 111, 238, 0.1)',
                                      color: companyStyles.companyColor
                                        ? companyStyles.companyColor
                                        : 'rgba(0, 111, 238, 0.9)',
                                    }
                                }
                                aria-label={`${isSelected ? 'Selected: ' : 'Select time slot: '
                                  }${slotLabel}`}
                                aria-pressed={isSelected}
                              >
                                <div className='flex items-center justify-between gap-2'>
                                  <div className='flex min-w-0 flex-1 items-center gap-2'>
                                    {/* Stacked avatars */}
                                    {(alwaysShowInspectors ||
                                      scheduleSettings.showInspectorInOnlineScheduler ||
                                      selectedInspectorIds.length > 0) && (
                                        <div className='flex shrink-0 -space-x-2'>
                                          {slot.inspectors
                                            .slice(0, 3)
                                            .map((inspector, idx) => (
                                              <Avatar
                                                key={inspector.id}
                                                src={inspector.thumb}
                                                alt={inspector.name}
                                                size='sm'
                                                className='h-6 w-6 ring-2 ring-white'
                                                style={{ zIndex: 3 - idx }}
                                              />
                                            ))}
                                        </div>
                                      )}

                                    <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
                                      <div className='flex items-center gap-1.5'>
                                        <span className='text-sm font-semibold'>
                                          {slotTime.format('h:mm A')}
                                        </span>
                                      </div>
                                      {(alwaysShowInspectors ||
                                        scheduleSettings.showInspectorInOnlineScheduler ||
                                        selectedInspectorIds.length > 0) && (
                                          <span className='truncate text-xs text-gray-600'>
                                            {slot.inspectors.length === 1
                                              ? slot.inspectors[0].name
                                              : `${slot.inspectors.length} available`}
                                          </span>
                                        )}
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <FaCheck
                                      size={14}
                                      aria-hidden='true'
                                      className='shrink-0'
                                    />
                                  )}
                                </div>
                              </button>

                              {/* Inspector dropdown for multi-inspector slots - show if alwaysShowInspectors is true OR toggle is enabled OR autoSelectTopInspector is false */}
                              {isExpanded && slot.inspectors.length > 1 && (!autoSelectTopInspector || alwaysShowInspectors || scheduleSettings.showInspectorInOnlineScheduler) && (
                                <div className='mt-2 rounded-lg border bg-white p-3 shadow-md'>
                                  <p className='mb-2 text-xs font-semibold text-gray-600'>
                                    Select Inspector:
                                  </p>

                                  <div className='space-y-1.5'>
                                    {/* Sort inspectors by score (highest first), then by name */}
                                    {[...slot.inspectors]
                                      .sort((a, b) => {
                                        // First sort by score (higher is better)
                                        if (a.score && b.score) {
                                          if (b.score !== a.score) {
                                            return b.score - a.score;
                                          }
                                        } else if (a.score && !b.score) {
                                          return -1;
                                        } else if (!a.score && b.score) {
                                          return 1;
                                        }
                                        // If scores are equal or both missing, sort by name
                                        return a.name.localeCompare(b.name);
                                      })
                                      .map((inspector) => {
                                        const isBest =
                                          inspector.id ===
                                          getBestInspectorForSlot(slot.datetime);
                                        const isSelected =
                                          selectedInspectorForSlot[slot.datetime] ===
                                          inspector.id;
                                        return (
                                          <button
                                            key={inspector.id}
                                            onClick={() =>
                                              handleInspectorSelect(
                                                slot.datetime,
                                                inspector.id
                                              )
                                            }
                                            className={`flex w-full items-center gap-2 rounded-lg p-2 text-left transition-colors ${isSelected
                                              ? 'bg-warning-50 border border-warning-300'
                                              : 'hover:bg-gray-50'
                                              }`}
                                          >
                                            <Avatar
                                              src={inspector.thumb}
                                              alt={inspector.name}
                                              size='sm'
                                              className='h-6 w-6 shrink-0'
                                            />
                                            <span
                                              className={`flex-1 text-sm ${isSelected
                                                ? 'font-semibold text-warning-900'
                                                : 'text-gray-700'
                                                }`}
                                            >
                                              {inspector.name}
                                            </span>
                                            {isBest && (
                                              <FaStar
                                                size={10}
                                                className='shrink-0 text-warning-500'
                                                aria-label='Top choice'
                                              />
                                            )}
                                            {isSelected && (
                                              <FaCheck
                                                size={10}
                                                className='shrink-0 text-warning-700'
                                                aria-label='Selected'
                                              />
                                            )}
                                          </button>
                                        );
                                      })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* No slots message */}
                    {!isLoading && !isPast && slotsForDay.length === 0 && (
                      <div className='py-2 text-center text-[10px] text-gray-400'>
                        No slots
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Desktop Calendar Grid */}
          <Card
            className={`hidden md:block ${isBackgroundLoading
              ? 'opacity-60 transition-opacity'
              : 'transition-opacity'
              }`}
          >
            <CardBody className='p-0'>
              <div role='grid' aria-label={`Calendar view for ${dateRange}`}>
                {/* First Week */}
                <div className='grid grid-cols-7 border-b' role='row'>
                  {days.slice(0, 7).map((day, index) => renderDayCell(day, index))}
                </div>

                {/* Second Week - only show if we have 14 days */}
                {calendarDays === 14 && (
                  <div className='grid grid-cols-7' role='row'>
                    {days
                      .slice(7, 14)
                      .map((day, index) => renderDayCell(day, index + 7))}
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Legend */}
          <div
            className='flex items-center justify-center gap-6 text-sm text-gray-600 mt-4'
            role='legend'
          >
            <div className='flex items-center gap-2'>
              <div
                className='h-4 w-4 rounded-full'
                style={{
                  backgroundColor:
                    companyStyles.colorSecondary ||
                    companyStyles.companyColor ||
                    '#006FEE',
                }}
                aria-hidden='true'
              ></div>
              <span>Today</span>
            </div>
            <div className='flex items-center gap-2'>
              <div
                className='h-4 w-4 rounded border'
                style={{
                  borderColor: companyStyles.colorSecondary
                    ? `${companyStyles.colorSecondary}33`
                    : companyStyles.companyColor
                      ? `${companyStyles.companyColor}33`
                      : 'rgba(0, 111, 238, 0.2)',
                  backgroundColor: companyStyles.colorSecondary
                    ? `${companyStyles.colorSecondary}0D`
                    : companyStyles.companyColor
                      ? `${companyStyles.companyColor}0D`
                      : 'rgba(0, 111, 238, 0.05)',
                }}
                aria-hidden='true'
              ></div>
              <span>Available slots</span>
            </div>
          </div>
        </>
        {emptyStateContent && (
          <div className='absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/30'>
            <div className='max-w-md px-4'>{emptyStateContent}</div>
          </div>
        )}
      </div>
    </div>
  );
}
